import {
  IUsageDataMRPCRequestParams,
  IUsageDataMRPCResponse,
} from 'multirpc-sdk';

import { MultiService } from 'modules/api/MultiService';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { web3Api } from 'store/queries';

import { REFETCH_STATS_INTERVAL, statsTimeframeToIntervalMap } from '../const';
import { StatsTimeframe } from '../types';
import { getStatsTimeBorders } from '../utils/getStatsTimeBorders';
// import publicStatsMock from './publicStatsMock.json';
import { filterByPriorityPairs } from '../utils/filterByPriorityPairs';

export interface IFetchPublicStatsParams {
  blockchains?: IUsageDataMRPCRequestParams['blockchains'];
  groupBy?: IUsageDataMRPCRequestParams['group_by'];
  limit?: number;
  metrics?: IUsageDataMRPCRequestParams['include_top_ofs'];
  timeframe: StatsTimeframe;
  timeframeShift?: number;
}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchPublicStats },
  useFetchPublicStatsQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchPublicStats: build.query<
      IUsageDataMRPCResponse,
      IFetchPublicStatsParams
    >({
      keepUnusedDataFor: REFETCH_STATS_INTERVAL,
      queryFn: createNotifyingQueryFn(
        async ({
          blockchains,
          groupBy,
          limit,
          metrics,
          timeframe,
          timeframeShift,
        }) => {
          const { end, start } = getStatsTimeBorders({
            timeframe,
            timeframeShift,
          });
          const interval = statsTimeframeToIntervalMap[timeframe];
          const intervalInSeconds = `${interval}s`;

          const api = MultiService.getService().getPublicGateway();

          const data = await api.getSharkTelemetry(
            {
              blockchains,
              end,
              include_top_ofs: metrics,
              interval: intervalInSeconds,
              start,
              top_limit: limit,
              group_by: groupBy,
            },
            { unfiltered: true },
          );

          return {
            data: {
              ...data,
              tops: filterByPriorityPairs(data.tops),
            },
          };

          // TODO: use for debugging:
          // const _data = publicStatsMock as IUsageDataMRPCResponse;

          // return {
          //   data: {
          //     ..._data,
          //     tops: filterByPriorityPairs(_data.tops)
          //   }
          // };
        },
      ),
    }),
  }),
  overrideExisting: true,
});

const fetchPublicStatsFallback: IUsageDataMRPCResponse = {
  all_time_total: 0,
  request_timelines: [],
  tops: [],
  total: 0,
  total_bytes: '0',
};

export const {
  selectDataWithFallbackCachedByParams: selectPublicStats,
  selectLoadingCachedByParams: selectPublicStatsLoading,
  selectStateCachedByParams: selectPublicStatsState,
} = createQuerySelectors({
  endpoint: fetchPublicStats,
  fallback: fetchPublicStatsFallback,
});
